import { UMBRALES_CALIDAD_IMAGEN_FACTURA_IA } from "./umbralesCalidadImagenFacturaIa";

/**
 * Analiza una foto de factura con canvas según umbrales Azure/Micromarket.
 *
 * @param {File|Blob} archivo imagen JPG/PNG.
 * @param {object} [umbrales] umbrales opcionales; por defecto los de Azure/app.
 * @returns {Promise<{
 *   esValida: boolean,
 *   errores: string[],
 *   advertencias: string[],
 *   ancho: number,
 *   alto: number,
 *   ladoMenor: number,
 *   brilloMedio: number|null
 * }>}
 */
export function analizarCalidadImagenFacturaIa(archivo, umbrales) {
  const limites = Object.assign({}, UMBRALES_CALIDAD_IMAGEN_FACTURA_IA, umbrales || {});
  return cargarImagenDesdeArchivo(archivo)
    .then((imagen) => evaluarImagenFacturaIa(imagen, archivo, limites))
    .catch(() => ({
      esValida: false,
      errores: [
        "No se pudo leer la foto \"" + obtenerNombreArchivo(archivo)
          + "\". Verifique que el archivo no esté dañado."
      ],
      advertencias: [],
      ancho: 0,
      alto: 0,
      ladoMenor: 0,
      brilloMedio: null
    }));
}

function evaluarImagenFacturaIa(imagen, archivo, limites) {
  const ancho = imagen.naturalWidth || imagen.width || 0;
  const alto = imagen.naturalHeight || imagen.height || 0;
  const ladoMenor = Math.min(ancho, alto);
  const nombre = obtenerNombreArchivo(archivo);
  const errores = [];
  validarRangoAzure(ancho, alto, nombre, limites, errores);
  if (!errores.length)
    validarLadoMenor(ladoMenor, nombre, limites, errores);
  return {
    esValida: errores.length === 0,
    errores: errores,
    advertencias: [],
    ancho: ancho,
    alto: alto,
    ladoMenor: ladoMenor,
    brilloMedio: null
  };
}

function validarRangoAzure(ancho, alto, nombre, limites, errores) {
  if (ancho < limites.dimensionMinimaAzure || alto < limites.dimensionMinimaAzure) {
    errores.push(
      "La foto \"" + nombre + "\" es demasiado pequeña. Use una imagen más nítida o acerque la cámara."
    );
    return;
  }
  if (ancho > limites.dimensionMaximaAzure || alto > limites.dimensionMaximaAzure) {
    errores.push(
      "La foto \"" + nombre + "\" es demasiado grande. Reduzca el tamaño o use otra captura."
    );
  }
}

function validarLadoMenor(ladoMenor, nombre, limites, errores) {
  if (ladoMenor < limites.ladoMenorMinimo) {
    errores.push(
      "La foto \"" + nombre + "\" es demasiado pequeña. Use una imagen más nítida o acerque la cámara."
    );
  }
}

function cargarImagenDesdeArchivo(archivo) {
  return new Promise((resolve, reject) => {
    if (!archivo) {
      reject(new Error("Archivo vacío"));
      return;
    }
    const url = URL.createObjectURL(archivo);
    const imagen = new Image();
    imagen.onload = () => {
      URL.revokeObjectURL(url);
      resolve(imagen);
    };
    imagen.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo cargar la imagen"));
    };
    imagen.src = url;
  });
}

function obtenerNombreArchivo(archivo) {
  if (archivo && archivo.name)
    return archivo.name;
  return "imagen";
}
